/**
 * Nearby grouping — pure & testable.
 *
 * Operates on ALREADY-RESOLVED locations; the async work (live-location reads,
 * geocoding the consent-gated shared hometown) happens in the useNearbyGroups
 * hook, which feeds resolved items in here. This keeps the algorithm portable
 * to native (no React, DOM, or storage).
 *
 * Algorithm (see docs/current/people-nearby.md § The Grouping Algorithm):
 *   1. Exclude items with no coords.
 *   2. Proximity band — everyone within radiusMi of the anchor (Haversine), any
 *      city. Rendered HEADERLESS (title: null) — the "Nearby" tab already names it.
 *   3. Far tier — cluster the rest by PROXIMITY (coordinates, single-linkage
 *      within radiusMi), NOT city strings, so same-metro name variants
 *      ("New York" / "New York City" / "Brooklyn") collapse together. Clusters
 *      with >= 2 members get a header labeled by their dominant city (ordered by
 *      distance from the anchor, nearest first); singletons roll into "Elsewhere
 *      in <countryCode>" (same country) / "International" (different), last.
 *   4. Within a group: met-timestamp desc.
 *   No anchor → one flat, headerless list (title: null), no grouping — we don't
 *   know where the viewer is, so grouping relative to them is meaningless.
 *
 * Groups with title === null render without a section header (see NearbyList).
 *
 * @typedef {Object} ResolvedItem
 * @property {string} chatRoomId
 * @property {number|null} lat
 * @property {number|null} lng
 * @property {string|null} city - canonical city (for grouping)
 * @property {string|null} countryCode
 * @property {number|string|null} metAt - meeting timestamp (within-group sort)
 *
 * @typedef {Object} NearbyGroup
 * @property {string} key
 * @property {string} title
 * @property {ResolvedItem[]} items
 */

export const NEARBY_RADIUS_MI = 25;

const EARTH_RADIUS_MI = 3958.8;

/** Great-circle distance between two points, in miles. */
export const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MI * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const toTime = (v) => {
  if (!v) return 0;
  const t = typeof v === 'number' ? v : Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
};
const byMetDesc = (a, b) => toTime(b.metAt) - toTime(a.metAt);

/**
 * @param {ResolvedItem[]} items
 * @param {{lat: number, lng: number, countryCode: string|null}|null} anchor
 * @param {{radiusMi?: number}} [options]
 * @returns {NearbyGroup[]}
 */
export const buildNearbyGroups = (items, anchor, options = {}) => {
  const radiusMi = options.radiusMi ?? NEARBY_RADIUS_MI;
  const viewerCountryCode = anchor?.countryCode ?? null;

  // 1. Only items with resolved coords
  const located = (items || []).filter((i) => i && i.lat != null && i.lng != null);
  const hasAnchor = !!(anchor && anchor.lat != null && anchor.lng != null);
  const groups = [];

  if (hasAnchor) {
    const distOf = (i) => haversineMiles(anchor.lat, anchor.lng, i.lat, i.lng);

    // 2. Proximity band — within radius, any city. Headerless (title: null):
    // the "Nearby" tab name already says it, so a "Near you" label would just
    // echo the tab. NearbyList skips groups with no title.
    const near = [];
    const far = [];
    for (const i of located) {
      if (distOf(i) <= radiusMi) near.push(i);
      else far.push(i);
    }
    if (near.length) {
      groups.push({ key: 'near-you', title: null, items: [...near].sort(byMetDesc) });
    }

    // 3. Far tier — cluster the rest by PROXIMITY (coordinates), not city
    // strings. Coordinates are the only reliable metro key: "New York" and
    // "New York City" are the same point, "Brooklyn" is ~5mi away — strings
    // split them, distance doesn't. Single-linkage within radiusMi; no city
    // database. Metro-aware / adaptive radius is Future Work.
    const clusterByProximity = (list) => {
      const clusters = [];
      const seen = new Array(list.length).fill(false);
      for (let i = 0; i < list.length; i++) {
        if (seen[i]) continue;
        const members = [list[i]];
        seen[i] = true;
        let grew = true;
        while (grew) {
          grew = false;
          for (let j = 0; j < list.length; j++) {
            if (seen[j]) continue;
            const linked = members.some(
              (m) => haversineMiles(m.lat, m.lng, list[j].lat, list[j].lng) <= radiusMi
            );
            if (linked) {
              members.push(list[j]);
              seen[j] = true;
              grew = true;
            }
          }
        }
        clusters.push(members);
      }
      return clusters;
    };

    // Label a cluster by its most common city (case-insensitive, original
    // spelling kept). Null if no member has a city name.
    const dominantCity = (members) => {
      const counts = new Map();
      const display = new Map();
      for (const m of members) {
        if (!m.city) continue;
        const k = m.city.toLowerCase();
        counts.set(k, (counts.get(k) || 0) + 1);
        if (!display.has(k)) display.set(k, m.city);
      }
      if (counts.size === 0) return null;
      const max = Math.max(...counts.values());
      const top = [...counts.keys()].filter((k) => counts.get(k) === max);
      if (top.length === 1) return display.get(top[0]);

      // Count tie → keep the label VIEWER-INDEPENDENT: pick the tied city nearest
      // the cluster's own centroid (a metro's central city is usually the major
      // one), then alphabetical as a deterministic final fallback. Picking the
      // truly "primary" city of a symmetric pair (LA vs Pasadena) needs
      // prominence data — Future Work; this just stops the label depending on
      // who's looking (it used to pick the city nearest the viewer).
      const cLat = members.reduce((s, m) => s + m.lat, 0) / members.length;
      const cLng = members.reduce((s, m) => s + m.lng, 0) / members.length;
      const toCentroid = (k) => {
        const m = members.find((x) => x.city && x.city.toLowerCase() === k);
        return haversineMiles(cLat, cLng, m.lat, m.lng);
      };
      top.sort((a, b) => {
        const d = toCentroid(a) - toCentroid(b);
        return Math.abs(d) > 1e-4 ? d : display.get(a).localeCompare(display.get(b));
      });
      return display.get(top[0]);
    };

    const farGroups = [];
    const singletons = [];
    clusterByProximity(far).forEach((members, idx) => {
      const title = dominantCity(members);
      if (members.length >= 2 && title) {
        farGroups.push({
          key: `city-${idx}-${title.toLowerCase()}`,
          title,
          items: [...members].sort(byMetDesc),
          distance: Math.min(...members.map(distOf)),
        });
      } else {
        singletons.push(...members);
      }
    });
    farGroups.sort((a, b) => a.distance - b.distance);
    for (const g of farGroups) groups.push({ key: g.key, title: g.title, items: g.items });

    // Singletons → catch-alls, always last
    if (singletons.length) {
      if (viewerCountryCode) {
        const domestic = singletons.filter((i) => i.countryCode === viewerCountryCode);
        const international = singletons.filter((i) => i.countryCode !== viewerCountryCode);
        if (domestic.length) {
          groups.push({
            key: 'elsewhere-domestic',
            title: `Elsewhere in ${viewerCountryCode}`,
            items: [...domestic].sort(byMetDesc),
          });
        }
        if (international.length) {
          groups.push({
            key: 'international',
            title: 'International',
            items: [...international].sort(byMetDesc),
          });
        }
      } else {
        groups.push({
          key: 'elsewhere',
          title: 'Elsewhere',
          items: [...singletons].sort(byMetDesc),
        });
      }
    }
  } else {
    // No anchor: we don't know where the viewer is, so grouping relative to them
    // is meaningless — one flat, headerless list (default met order). The UI's
    // "set your hometown / share location" prompt is how they enable grouping.
    if (located.length) {
      groups.push({ key: 'all', title: null, items: [...located].sort(byMetDesc) });
    }
  }

  return groups;
};
